import { Component, ElementRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import type { Animation } from '@ionic/angular';

import { ComponentAdapterFactory } from "../../class/adapters/component-adapter-factory";

import { IconHelper } from "../../class/icon-helper";
import { WidgetParent } from "../../class/widget-parent";
import { ThermostatHVACMode } from "../../enum/enumThermostatHVACMode";
import { ISliderColor } from "../../interfaces/ISliderColor";
import { IWidgetExpandable } from "../../interfaces/IWidgetExpandable";
import { SmartPaletteGeneral } from "../../class/smart-palette";
import { ComponentAdapterThermostat } from "../../class/adapters/component-adapter-thermostat";
import { Subscription } from "rxjs";
import { debounceTime, filter } from "rxjs/operators";
import { StaticClass } from "../../class/general-classes/static-classes";
import { IonIcon } from "@ionic/angular";
import { DraggableSliderComponent } from "../draggable-slider/draggable-slider.component";
import { ThermostatExpandedSectionComponent } from "../thermostat/thermostat-expanded-section/thermostat-expanded-section.component";
import { SmartComponentSpinnerComponent } from "../smart-component-spinner/smart-component-spinner.component";
import { ErrorLoadingWidgetComponent } from "../error-loading-widget/error-loading-widget.component";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
    selector: "app-widget-thermostat",
    templateUrl: "./widget-thermostat.component.html",
    styleUrls: ["./widget-thermostat.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonIcon, DraggableSliderComponent, ThermostatExpandedSectionComponent, SmartComponentSpinnerComponent, ErrorLoadingWidgetComponent, TranslatePipe]
})

/**
 * Counter entry componet
 */
export class WidgetThermostatComponent
  extends WidgetParent<ComponentAdapterThermostat>
  implements IWidgetExpandable {
  private _isHvacSingleBitProtection: boolean = false;
  private _isHvacSingleBitEconomy: boolean = false;
  private _isHvacSingleBitConfort: boolean = false;

  public iconThermostat = ""; //

  public iconSeason: string = "";
  public iconHvacMode: string = "";

  public colorSliderLighter: string = "#00d1ff";
  public colorSliderDarker: string = "#183e91";

  public actualTemperatureValue: string = "0";
  public setpointBaseValue: number = 0;
  public actualSetpointValue: number = 0;
  public shiftSetpointValue: number = 0;
  public shiftSetpointMinValue: number = 0;

  public isWinter: boolean = false;
  public isValveOpen: boolean = false;
  public isWindowsOpen: boolean = false;
  public hvacStateMode: ThermostatHVACMode = ThermostatHVACMode.COMFORT;

  public hvacModeValues: { [key: string]: number } = {};

  public isExpanded: boolean = false;

  public winterColor: ISliderColor = {
    lighter: "#FF9900",
    darker: "#9E0505",
  };

  public summerColor: ISliderColor = {
    lighter: "#00d1ff",
    darker: "#19398E",
  };

  public cssSeason: string = "";

  public currentSeasonColor: ISliderColor = this.summerColor;
  public numberOfSteps = 0.1;

  //Value of FancoilCommand
  public fancoilSpeedValue: number = 0;

  //Value of FancoilState
  public fancoilStateValue: number = 0;

  //Value of selected speed for single speed mode
  public currentSelectedSpeed: number = 0;

  public isFancoilCommandActive: boolean = false;
  public areStateAndCommandEqual: boolean = false;

  public singleSpeedMode: boolean = false;
  public singleSpeedList: Array<number> = [0];
  public singleSpeedSliderMax: number = 0;

  private _fancoilAnimation: Animation;
  private _highestSpeedAnimationDuration: number = 500;
  private _fancoilAnimationDuration: number = 0;
  private _widgetIsDisplayableSub: Subscription = null;
  public automaticFancoilMode: boolean = false;

  private _openWindowAnimation: Animation;

  public iconHelper = IconHelper;

  constructor() {
    super();

    this._logContext = "Widget-thermostat.component";
  }

  @ViewChild("fancoilIcon", { read: ElementRef }) _fancoilIcon: ElementRef;
  @ViewChild("openWindowIcon", { read: ElementRef }) _openWindowIcon: ElementRef;

  public ngAfterViewInit(): void {

    this._widgetIsDisplayableSub = this._widgetIsDisplayable.pipe(
      filter(isReady => isReady),
      debounceTime(500),
    ).subscribe(isReady => {

      //animate icon fancoil
      this.runFancoilAnimation(this.getCorrectValueFancoilSpeed());

      //animate icon open window
      if (this.isWindowsOpen) {
        this.createWindowAnimation();
      }
    });
  }

  /**
   * Check if the command setpoint is available to enable drag on component for setpoint
   *
   * @returns Is available or not
   */
  public isCommandSetpointAvailable(): boolean {
    return !(
      (!this.componentAdapter.SetpointBase &&
        !this.componentAdapter.HvacState &&
        !this.componentAdapter.HeatingCoolingCommand &&
        !this.componentAdapter.HeatingCoolingState) ||
      (!this.componentAdapter[this.getCurrentCommandName()] &&
        !this.componentAdapter.SetpointBase)
    );
  }

  /**
   * Handle base click to collapse or expand the widget
   */
  public onClickOpenCloseWidget(): void {
    if (this.isExpanded) {
      this.onCollapseWidget();
    } else {
      this.onExpandWidget();
    }
  }

  public onExpandWidget(): void {
    this.isExpanded = true;
  }

  public onCollapseWidget(): void {
    this.isExpanded = false;
  }

  /**
   * Handle callback event for slide actual setpoint
   *
   * @param value value received from slide
   */
  public onSlideSetpoint(value: number): void {

    if (
      (this.componentAdapter.HvacState || (this.componentAdapter.ComfortSwitch || this.componentAdapter.ProtectSwitch ||
        this.componentAdapter.StandbySwitch || this.componentAdapter.EconomySwitch)) &&
      (this.componentAdapter.HeatingCoolingCommand ||
        this.componentAdapter.HeatingCoolingState ||
        this.componentAdapter.HasUniqueSetpointForSeason)
    ) {
      if (value !== this.actualSetpointValue) {
        this.sendThermostatCommand(value, this.getCurrentCommandName());
      }
    } else if (this.componentAdapter.SetpointBase !== null) {
      if (value !== this.setpointBaseValue) {
        this.sendThermostatCommand(value, "SetpointBase");
      }
    }
  }

  /**
   * Handle callback event for slide shift setpoint
   *
   * @param value value received from slide
   */
  public onSlideShiftSetpoint(value: number): void {
    if (value !== this.shiftSetpointValue) {
      this.sendThermostatCommand(value, "ShiftSetpoint");
    }
  }

  /**
   * Check if the fancoil icon is visible
   * @returns true or false
   */
  public showFancoilIcon(): boolean {
    if (this.componentAdapter.FancoilState || this.componentAdapter.FancoilV1 || this.componentAdapter.FancoilV2 || this.componentAdapter.FancoilV3) {
      return true;
    }
    return false;
  }

  /**
   * Handle callback event for slide fan
   *
   * @param value Value received
   */
  public onSlideFan(value: number): void {

    if (this.componentAdapter.FancoilCommand) {
      if (value !== this.fancoilSpeedValue) {

        this.sendThermostatCommand(value, "FancoilCommand");
      }
    }

    if (!this.componentAdapter.FancoilCommand && (this.componentAdapter.FancoilV0 || this.componentAdapter.FancoilV1 || this.componentAdapter.FancoilV2 || this.componentAdapter.FancoilV3)) {
      //for single speed mode the value is the slider position, we need to get the value written on the labels
      const speedValue = this.singleSpeedList[value];
      if (speedValue !== this.currentSelectedSpeed) {
        let currentSelectedSpeedCommand = "FancoilV" + this.currentSelectedSpeed.toString();

        //Send command to turn off the current selected speed
        if (this.componentAdapter[currentSelectedSpeedCommand]) {
          this.sendThermostatCommand(0, currentSelectedSpeedCommand);
        }

        //Change currentSelectedSpeed with the new value
        this.currentSelectedSpeed = speedValue;
        currentSelectedSpeedCommand = "FancoilV" + this.currentSelectedSpeed.toString();

        //Send command to turn on the new selected speed
        if (this.componentAdapter[currentSelectedSpeedCommand]) {
          this.sendThermostatCommand(1, currentSelectedSpeedCommand);
        }
      }

      //if AutoManCommandFancoil exists and it's turned on, turn off automatic fancoil mode
      if (this.componentAdapter.AutoManCommandFancoil && this.automaticFancoilMode) {
        const instanceGUID: string = this.componentAdapter.AutoManCommandFancoil.instanceGUID;
        const address: string = this.componentAdapter.AutoManCommandFancoil.address;

        this.sendAddressCommand(instanceGUID, address, "0");
      }
    }
  }

  /**
   * Get the class for hvac mode icon in order to use a different color for each mode (used in Vimar theme)
   * @returns string with css class
   */
  public setCssForHvacIcon(): string {
    switch (this.hvacStateMode) {
      case ThermostatHVACMode.COMFORT:
        return "comfort";
      case ThermostatHVACMode.STANDBY:
        return "standby";
      case ThermostatHVACMode.ECO:
        return "eco";
      case ThermostatHVACMode.PROTECTION:
        return "protection";
    }
  }

  /**
   * Run animation for fancoil icon
   * @param value of slider
   */
  private runFancoilAnimation(value: number): void {
    const maxValue = this.getMinOrMaxValueFancoil('Max');

    // Reset animation before changing duration
    this.resetFancoilAnimation();

    if (value > 0) {
      // Percentage of empty part of the slider
      const percentage = (maxValue - value) / maxValue;
      this._fancoilAnimationDuration = this._highestSpeedAnimationDuration + (this._highestSpeedAnimationDuration * 2 * percentage);

      // Restart animation after changing duration
      this.createFancoilAnimation();
    }
  }

  /**
   * Create animation fancoil icon
   */
  private createFancoilAnimation(): void {
    this._fancoilAnimation = this._animationController
      .create()
      .addElement(this._fancoilIcon.nativeElement)
      .duration(this._fancoilAnimationDuration)
      .iterations(Infinity)
      .fromTo('rotate', '0deg', '360deg');

    this._fancoilAnimation.play();
  }

  /**
   * Create animation for open window icon
   */
  private createWindowAnimation(): void {
    this._openWindowAnimation = this._animationController
      .create()
      .addElement(this._openWindowIcon.nativeElement)
      .duration(1500)
      .iterations(Infinity)
      .direction("alternate")
      .fromTo('opacity', 1, 0.3);

    this._openWindowAnimation.play();
  }

  /**
   * Stop and reset fancoil icon animation
   */
  private resetFancoilAnimation(): void {
    this._fancoilAnimation?.stop();
    this._fancoilAnimation = null;
  }

  /**
   * Send command to mqtt for temperature or fancoil
   *
   * @param value Value to set over the selected command address
   */
  private sendThermostatCommand(value: number, commandName: string) {
    const instanceGUID: string = this.componentAdapter[commandName]?.instanceGUID;
    const address: string = this.componentAdapter[commandName]?.address;

    //Do not publish anything if the skill is not configured, otherwise an invalid topic would be written
    if (!instanceGUID || !address) {
      return;
    }

    this.sendAddressCommand(instanceGUID, address, value.toString());
  }

  /**
   * Get the min/max value of the current mode or for base setpoint
   * @param minOrMax string "Min" o "Max" to append to the name of the setpoint
   * @returns min or max value
   */
  public getMinOrMaxValueTemperature(minOrMax: "Min" | "Max"): number {
    let setpointName: string = "Setpoint";

    if (this.componentAdapter.SetpointBase) {
      setpointName += "Base" + minOrMax;

      return parseFloat(this.componentAdapter[setpointName]);
    } else if (this.componentAdapter[this.getCurrentCommandName()]) {
      setpointName += this.getCurrentCommandName() + minOrMax;

      return parseFloat(this.componentAdapter[setpointName]);
    }
  }

  /**
   * Check if the slider is draggable
   * @returns true or false
   */
  public isFancoilSliderActive(): boolean {
    // The slider is active if the flag areStateAndCommandEqual is true
    if (this.areStateAndCommandEqual) {
      return true;
    }
    return false;
  }

  /**
   * Get the min/max value of the fancoil
   * @param minOrMax string "Min" o "Max" to append to the name of the command 
   * @returns min or max value
   */
  public getMinOrMaxValueFancoil(minOrMax: "Min" | "Max"): number {
    let commandName = "Fancoil"

    if (!this.singleSpeedMode) {
      if (this.componentAdapter.FancoilCommandMin === this.componentAdapter.FancoilStateMin &&
        this.componentAdapter.FancoilCommandMax === this.componentAdapter.FancoilStateMax) {

        commandName += "Command" + minOrMax;
        return parseFloat(this.componentAdapter[commandName]);
      }
    }

    if (this.singleSpeedMode && this.areStateAndCommandEqual) {
      if (minOrMax === "Min") {
        return 0;
      } else {
        return this.singleSpeedSliderMax;
      }
    }

    commandName += "State" + minOrMax;
    return parseFloat(this.componentAdapter[commandName]);
  }

  /**
   * Get the correct value of fancoil speed according to active mode
   * @returns value variable
   */
  public getCorrectValueFancoilSpeed(): number {
    if (this.areStateAndCommandEqual && this.singleSpeedMode) {
      return this.currentSelectedSpeed;
    }

    return this.fancoilStateValue;
  }

  /**
   * Get the name of the selected HVAC Mode
   * @returns string
   */
  private getCurrentCommandName(): string {
    let commandName: string = this.isWinter ? "Heating" : "Cooling";

    switch (this.hvacStateMode) {
      case ThermostatHVACMode.PROTECTION:
        commandName += "Protection";
        break;
      case ThermostatHVACMode.ECO:
        commandName += "Economy";
        break;
      case ThermostatHVACMode.COMFORT:
        commandName += "Comfort";
        break;
      case ThermostatHVACMode.STANDBY:
        commandName += "Standby";
        break;
      default:
        commandName += "";
    }
    return commandName;
  }

  /**
   * If state and command are equal set flag to true
   */
  private checkIfStateAndCommandAreEqual(): void {
    if (this.componentAdapter.FancoilCommand && this.componentAdapter.FancoilCommandMin === this.componentAdapter.FancoilStateMin &&
      this.componentAdapter.FancoilCommandMax === this.componentAdapter.FancoilStateMax || !this.componentAdapter.FancoilState) {
      this.areStateAndCommandEqual = true;
    }
  }

  protected setWidgetSmartPalette(): SmartPaletteGeneral {
    let widgetSmaertPalette = new SmartPaletteGeneral();

    return widgetSmaertPalette;
  }

  protected onComponentLoad(): void {
    this.updateSeasonVariables();
    this.setHvacModeIconState();
  }

  protected onComponentDestroy(): void {
    this.resetFancoilAnimation();
    this._widgetIsDisplayableSub?.unsubscribe();
  }

  protected onSmartComponentErrorLoading(): void { }

  protected onSmartComponentSuccessLoaded(): void | boolean {
    const factory = new ComponentAdapterFactory();

    this.componentAdapter = factory.createComponentAdapter(
      this._listSmartComponentSkills,
      ComponentAdapterThermostat
    );
    this.titleWidget = this.componentAdapter.Name;

    //If component title is empty show error loading component
    if (!this.titleWidget) {
      return this._errorLoading = true;
    }

    this.isExpanded = this._isExpanded;

    // Set isFancoilCommandActive to true if at least one fancoil command is active
    if (this.componentAdapter.FancoilCommand ||
      this.componentAdapter.FancoilV0 || this.componentAdapter.FancoilV1 ||
      this.componentAdapter.FancoilV2 || this.componentAdapter.FancoilV3) {
      this.isFancoilCommandActive = true;
    }

    //Check if state and command are the same for fancoil
    this.checkIfStateAndCommandAreEqual();

    //Check if single speed mode is available for fancoil
    this.checkIfSingleSpeedModeIsActive();
    this.checkAvailabilitySingleSpeed();

    if (this.componentAdapter.HasUniqueSetpointForSeason) {
      //Set iswinter to true to take the 'heating' setpoints
      this.isWinter = true;
      //if I have only one setpoint I set the color to blue (typically used for cooling)
      this.currentSeasonColor = this.summerColor;
      this.winterColor = this.summerColor;
    }

    if (this.componentAdapter.ShiftSetpoint) {
      this.shiftSetpointMinValue = -parseFloat(this.componentAdapter.ShiftSetpointMin);
    }
  }

  protected onAddressValueChange(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (this.componentAdapter.ActualTemperature) {
      this.tryToUpdateTemperatureValue(instanceGUID, address, value);
    }

    this.handleHvacStateChange(instanceGUID, address, value);

    if (
      (this.componentAdapter.HvacState || (this.componentAdapter.ComfortSwitch || this.componentAdapter.ProtectSwitch ||
        this.componentAdapter.StandbySwitch || this.componentAdapter.EconomySwitch)) &&
      (this.componentAdapter.HeatingCoolingCommand ||
        this.componentAdapter.HeatingCoolingState ||
        this.componentAdapter.HasUniqueSetpointForSeason)
    ) {
      this.tryToUpdateTemperatureValueIfModeisActive(
        instanceGUID,
        address,
        value,
        "CoolingComfort"
      );
      this.tryToUpdateTemperatureValueIfModeisActive(
        instanceGUID,
        address,
        value,
        "CoolingProtection"
      );
      this.tryToUpdateTemperatureValueIfModeisActive(
        instanceGUID,
        address,
        value,
        "CoolingStandby"
      );
      this.tryToUpdateTemperatureValueIfModeisActive(
        instanceGUID,
        address,
        value,
        "CoolingEconomy"
      );
      this.tryToUpdateTemperatureValueIfModeisActive(
        instanceGUID,
        address,
        value,
        "HeatingComfort"
      );
      this.tryToUpdateTemperatureValueIfModeisActive(
        instanceGUID,
        address,
        value,
        "HeatingProtection"
      );
      this.tryToUpdateTemperatureValueIfModeisActive(
        instanceGUID,
        address,
        value,
        "HeatingStandby"
      );
      this.tryToUpdateTemperatureValueIfModeisActive(
        instanceGUID,
        address,
        value,
        "HeatingEconomy"
      );
    }

    if (this.componentAdapter.HeatingCoolingState) {
      this.tryToUpdateSeasonValue(instanceGUID, address, value);
    }

    if (this.componentAdapter.WindowsOpen) {
      this.tryToUpdateWindowsOpenValue(instanceGUID, address, value);
    }

    if (this.componentAdapter.ValveState) {
      this.tryToUpdateValveStateValue(instanceGUID, address, value);
    }

    if (this.componentAdapter.ActualSetpointOrSummerSetpoint) {
      this.tryToUpdateActualSetpointValue(instanceGUID, address, value);
    }

    if (this.componentAdapter.ShiftSetpoint) {
      this.tryToUpdateShiftSetpointValue(instanceGUID, address, value);
    }

    if (this.componentAdapter.SetpointBase) {
      this.tryToUpdateBaseSetpointValue(instanceGUID, address, value);
    }

    if (this.componentAdapter.FancoilCommand || this.componentAdapter.FancoilV0 || this.componentAdapter.FancoilV1 ||
      this.componentAdapter.FancoilV2 || this.componentAdapter.FancoilV3) {
      this.tryToUpdateFancoilValue(instanceGUID, address, value);
    }

    if (this.componentAdapter.FancoilState && instanceGUID === this.componentAdapter.FancoilState.instanceGUID &&
      address === this.componentAdapter.FancoilState.address) {
      this.fancoilStateValue = parseInt(value);
    }

    if (this.componentAdapter.AutoManStateFancoil) {
      this.tryToUpdateAutoManFancoilState(instanceGUID, address, value);
    }

    if (this._fancoilIcon) {
      //Update animation speed when fancoil speed changes
      this.runFancoilAnimation(this.getCorrectValueFancoilSpeed());
    }

    //animate icon open window
    if (this.isWindowsOpen) {
      setTimeout(() => this.createWindowAnimation(), 500);
    } else {
      this._openWindowAnimation?.stop();
      this._openWindowAnimation = null;
    }
  }

  /**
   * It defines the single speeds that there are available
   */
  private checkAvailabilitySingleSpeed(): void {
    if (!this.componentAdapter.FancoilCommand) {
      if (this.componentAdapter.FancoilV1) {
        this.singleSpeedList.push(1);
      }
      if (this.componentAdapter.FancoilV2) {
        this.singleSpeedList.push(2);
      }
      if (this.componentAdapter.FancoilV3) {
        this.singleSpeedList.push(3);
      }

      //set the max value of slider (not the value of max speed) based of which skills are available
      this.singleSpeedSliderMax = this.singleSpeedList.length - 1;
    }
  }

  /**
   * If fancoil command is not active and the single speed mode is, check the flag singleSpeedMode to true
   */
  private checkIfSingleSpeedModeIsActive(): void {
    // Single speed mode is active if any FancoilV exists and FancoilCommand doesn't exist
    if (!this.componentAdapter.FancoilCommand && (this.componentAdapter.FancoilV0 || this.componentAdapter.FancoilV1 ||
      this.componentAdapter.FancoilV2 || this.componentAdapter.FancoilV3)) {
      this.singleSpeedMode = true;
    }
  }

  /**
   * Handles changes to the HVAC mode from the associated state addresses.
   * To determine exactly which address(es) are to be considered, it check if the address "HvacState" have been initialised. If so,
   * the method reacts to it; otherwise, it considers the (state) addresses associated to the single HVAC Operative modes.
   * In this last case, after having considered all possibile value changes, it checks this valorization in its entirety in order to
   * define the actual HVAC Operative Mode of the thermostat.
   * @param instanceGUID
   * @param address
   * @param value
   */
  private handleHvacStateChange(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (this.componentAdapter.HvacState) {
      this.tryToUpdateHvacStateValueGeneral(instanceGUID, address, value);
    } else {
      this.tryToUpdateHvacStateValueSingleModeProtect(
        instanceGUID,
        address,
        value
      );

      this.tryToUpdateHvacStateValueSingleModeConfort(
        instanceGUID,
        address,
        value
      );

      this.tryToUpdateHvacStateValueSingleModeEconomy(
        instanceGUID,
        address,
        value
      );

      this.tryToApplyHvacStateValueSingleMode();
    }
  }

  /**
   * Set the flag automaticFancoilMode to true if its command is on
   * @param instanceGUID 
   * @param address 
   * @param value 
   */
  private tryToUpdateAutoManFancoilState(instanceGUID: string, address: string, value: string) {
    if (
      this.componentAdapter.AutoManStateFancoil?.instanceGUID === instanceGUID &&
      this.componentAdapter.AutoManStateFancoil?.address === address
    ) {
      this.automaticFancoilMode = parseInt(value) === 1;
    }
  }

  /**
   * Check if (state) address associated to the single HVAC Operative mode "Protected" is initialised; if so, it stores locally the currrent value.
   * This will be used to define the actual HVAC Operative Mode of the Thermosat (if the skills of the single modes are being used).
   * @param instanceGUID
   * @param address
   * @param value
   */
  private tryToUpdateHvacStateValueSingleModeProtect(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      this.componentAdapter.ProtectSwitch?.instanceGUID === instanceGUID &&
      this.componentAdapter.ProtectSwitch?.address === address
    ) {
      this._isHvacSingleBitProtection = parseInt(value) === 1;
    }
  }

  /**
   * Check if (state) address associated to the single HVAC Operative mode "Economy" is initialised; if so, it stores locally the currrent value.
   * This will be used to define the actual HVAC Operative Mode of the Thermosat (if the skills of the single modes are being used).
   * @param instanceGUID
   * @param address
   * @param value
   */
  private tryToUpdateHvacStateValueSingleModeEconomy(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      this.componentAdapter.EconomySwitch?.instanceGUID === instanceGUID &&
      this.componentAdapter.EconomySwitch?.address === address
    ) {
      this._isHvacSingleBitEconomy = parseInt(value) === 1;
    }
  }

  /**
   * Check if (state) address associated to the single HVAC Operative mode "Comfort" is initialised; if so, it stores locally the currrent value.
   * This will be used to define the actual HVAC Operative Mode of the Thermosat (if the skills of the single modes are being used).
   * @param instanceGUID
   * @param address
   * @param value
   */
  private tryToUpdateHvacStateValueSingleModeConfort(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      this.componentAdapter.ComfortSwitch?.instanceGUID === instanceGUID &&
      this.componentAdapter.ComfortSwitch?.address === address
    ) {
      this._isHvacSingleBitConfort = parseInt(value) === 1;
    }
  }

  /**
   * This method defines the actual HVAC Operative Mode of the thermostat in cases where the skills of the single modes are being used.
   * To do so, it check the current values associated to each "HAVC Mode binary bit" (IE. the boolean local variables used to represents the fact that
   * a peculiar single HVAC mode is being selected or not) each time a value change is received on one of them, following the gerarchy here detailed:
   *
   * PROTECTION <--- ECONOMY <--- COMFORT <--- STANDBY
   *
   * In this way, the HVAC mode applied to the thermostat widget will be always the correct one, regardless the order the value-changes are received.
   */
  private tryToApplyHvacStateValueSingleMode(): void {
    let currentHvacMode: ThermostatHVACMode = ThermostatHVACMode.STANDBY;

    if (this._isHvacSingleBitProtection) {
      currentHvacMode = ThermostatHVACMode.PROTECTION;
    } else if (this._isHvacSingleBitEconomy) {
      currentHvacMode = ThermostatHVACMode.ECO;
    } else if (this._isHvacSingleBitConfort) {
      currentHvacMode = ThermostatHVACMode.COMFORT;
    } else {
      currentHvacMode = ThermostatHVACMode.STANDBY;
    }

    this.hvacStateMode = currentHvacMode;

    this.setHvacModeIconState();
  }

  /**
   * Try to update actual setpoint value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value value to test
   */
  private tryToUpdateActualSetpointValue(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    const actualSetpoint = this.isWinter && this.componentAdapter.ActualWinterSetpoint
      ? this.componentAdapter.ActualWinterSetpoint
      : this.componentAdapter.ActualSetpointOrSummerSetpoint;

    if (
      actualSetpoint &&
      instanceGUID === actualSetpoint.instanceGUID &&
      address === actualSetpoint.address
    ) {
      this.actualSetpointValue = parseFloat(value);
    }
  }

  /**
   * Try to update shift setpoint value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value value to test
   */
  private tryToUpdateShiftSetpointValue(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.ShiftSetpoint.instanceGUID &&
      address === this.componentAdapter.ShiftSetpoint.address
    ) {
      this.shiftSetpointValue = parseFloat(value);
    }
  }

  /**
 * Try to update base setpoint value
 *
 * @param instanceGUID Instance GUID to test
 * @param address address to test
 * @param value value to test
 */
  private tryToUpdateBaseSetpointValue(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.SetpointBase.instanceGUID &&
      address === this.componentAdapter.SetpointBase.address
    ) {
      this.setpointBaseValue = parseFloat(value);
    }
  }

  /**
   * Try to update fancoil speed value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value value to test
   */
  private tryToUpdateFancoilValue(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (this.componentAdapter.FancoilCommand && instanceGUID === this.componentAdapter.FancoilCommand.instanceGUID &&
      address === this.componentAdapter.FancoilCommand.address) {
      this.fancoilSpeedValue = parseInt(value);
    } else if (this.componentAdapter.FancoilV0 && instanceGUID === this.componentAdapter.FancoilV0.instanceGUID &&
      address === this.componentAdapter.FancoilV0.address && parseInt(value) === 1) {
      this.currentSelectedSpeed = 0;
    } else if (this.componentAdapter.FancoilV1 && instanceGUID === this.componentAdapter.FancoilV1.instanceGUID &&
      address === this.componentAdapter.FancoilV1.address && parseInt(value) === 1) {
      this.currentSelectedSpeed = 1;
    } else if (this.componentAdapter.FancoilV2 && instanceGUID === this.componentAdapter.FancoilV2.instanceGUID &&
      address === this.componentAdapter.FancoilV2.address && parseInt(value) === 1) {
      this.currentSelectedSpeed = 2;
    } else if (this.componentAdapter.FancoilV3 && instanceGUID === this.componentAdapter.FancoilV3.instanceGUID &&
      address === this.componentAdapter.FancoilV3.address && parseInt(value) === 1) {
      this.currentSelectedSpeed = 3;
    }
  }

  /**
   * Try to update valve state value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value value to test
   */
  private tryToUpdateValveStateValue(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.ValveState.instanceGUID &&
      address === this.componentAdapter.ValveState.address
    ) {
      this.isValveOpen = value === "1";
    }
  }

  /**
   * Try to test received value and update the season value
   *
   * @param instanceGUID Instance guid to test
   * @param address Address to test
   * @param value value to test
   */
  private tryToUpdateWindowsOpenValue(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.WindowsOpen.instanceGUID &&
      address === this.componentAdapter.WindowsOpen.address
    ) {
      this.isWindowsOpen = value === "1";
    }
  }

  /**
   * Try to test received value and update the season value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateSeasonValue(
    instanceGUID: string,
    address: string,
    value: string
  ) {
    if (
      instanceGUID === this.componentAdapter.HeatingCoolingState.instanceGUID &&
      address === this.componentAdapter.HeatingCoolingState.address
    ) {
      this.isWinter = value === "1";

      this.updateSeasonVariables();
      this.getActualSetpointValueBySeason();
    }
  }

  /**
   * Refresh the actual setpoint value based on the current season
   */
  private getActualSetpointValueBySeason(): void {
    const actualSetpoint = this.isWinter && this.componentAdapter.ActualWinterSetpoint
      ? this.componentAdapter.ActualWinterSetpoint
      : this.componentAdapter.ActualSetpointOrSummerSetpoint;

    const currentAddress = this._addressService.getAddressByInstanceGUIDAndAddress(
      actualSetpoint.instanceGUID,
      actualSetpoint.address
    );

    if (currentAddress?.lastValue !== "") {
      const parsedValue = parseFloat(currentAddress.lastValue);
      if (!isNaN(parsedValue)) {
        this.actualSetpointValue = parsedValue;
      }
    }
  }

  /**
   * Try to test received value and update the temperature value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateTemperatureValue(
    instanceGUID: string,
    address: string,
    value: string
  ) {
    if (this.componentAdapter.ActualTemperature) {
      // Update actual temperature to show
      if (
        instanceGUID === this.componentAdapter.ActualTemperature.instanceGUID &&
        address === this.componentAdapter.ActualTemperature.address
      ) {
        this.actualTemperatureValue = StaticClass.formatDecimalValue(value, 1, true);
      }
    }
  }

  /**
   * Try to update current value to show slider hvac modes
   *
   * @param instanceGUID
   * @param address
   * @param value
   */
  private tryToUpdateTemperatureValueIfModeisActive(
    instanceGUID: string,
    address: string,
    value: string,
    commandName: string
  ) {
    if (this.componentAdapter[commandName]) {
      if (
        instanceGUID === this.componentAdapter[commandName].instanceGUID &&
        address === this.componentAdapter[commandName].address
      ) {
        switch (commandName) {
          case "CoolingComfort":
            this.hvacModeValues.coolingComfort = parseFloat(value);
            break;
          case "CoolingProtection":
            this.hvacModeValues.coolingProtection = parseFloat(value);
            break;
          case "CoolingStandby":
            this.hvacModeValues.coolingStandby = parseFloat(value);
            break;
          case "CoolingEconomy":
            this.hvacModeValues.coolingEconomy = parseFloat(value);
            break;
          case "HeatingComfort":
            this.hvacModeValues.heatingComfort = parseFloat(value);
            break;
          case "HeatingProtection":
            this.hvacModeValues.heatingProtection = parseFloat(value);
            break;
          case "HeatingStandby":
            this.hvacModeValues.heatingStandby = parseFloat(value);
            break;
          case "HeatingEconomy":
            this.hvacModeValues.heatingEconomy = parseFloat(value);
            break;
        }
      }
    }
  }

  /**
   * Try to test received value and update the HVAC state value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateHvacStateValueGeneral(
    instanceGUID: string,
    address: string,
    value: string
  ) {
    if (
      instanceGUID === this.componentAdapter.HvacState.instanceGUID &&
      address === this.componentAdapter.HvacState.address
    ) {
      this.hvacStateMode = parseInt(value);
      this.setHvacModeIconState();
    }
  }
  /**
   * Update the variables based on the season
   */
  private updateSeasonVariables(): void {
    this.setProgressColorBySeason();
    this.setIconSeason();
    this.setCssSeason();
  }

  /**
   * Set the icon for the season
   */
  private setIconSeason(): void {
    this.iconSeason = this.isWinter ? this.iconHelper.getIconsForThermostat().winter : this.iconHelper.getIconsForThermostat().summer;
  }

  /**
   * Set the css class for the season
   */
  private setCssSeason(): void {
    this.cssSeason = this.isWinter ? "winter" : "summer";
  }

  /**
   * Get color for the progress bar by season
   */
  private setProgressColorBySeason(): void {
    this.currentSeasonColor = this.isWinter
      ? this.winterColor
      : this.summerColor;
  }

  /**
   * Set the icon for the HVAC mode
   */
  private setHvacModeIconState(): void {
    this.iconHvacMode = IconHelper.getIconHvacModeByMode(this.hvacStateMode);
  }
}
